import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from '../../api/axios';
import { useAuth } from '../../contexts/AuthContext';
import ApiErrorModal from '../../components/ApiErrorModal';
import { DocumentIcon, ArrowRightIcon } from '../../components/Icons'; // Using ArrowRight and rotating it for back, or just text

export default function BankDetailsView() {
  const { id, accountIndex } = useParams();
  const navigate = useNavigate();
  const [bank, setBank] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorModal, setErrorModal] = useState({ isOpen: false, title: "", message: "", details: null });

  useEffect(() => {
    const controller = new AbortController();
    fetchBankDetails(controller.signal);
    return () => controller.abort();
  }, [id]);

  const fetchBankDetails = async (signal) => {
    setLoading(true);
    try {
      const res = await axios.get(`/users/me/bank-accounts/${id}`, { signal });
      if (res.data?.bank_account) {
        setBank(res.data.bank_account);
      } else {
        setErrorModal({
          isOpen: true,
          title: "Not Found",
          message: "The requested bank account could not be found.",
        });
      }
    } catch (e) {
      if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
      setErrorModal({
        isOpen: true,
        title: "Error",
        message: "Failed to load bank account details.",
        details: e.response?.data?.message || e.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/u/${accountIndex || 0}/dashboard/profile`);
  };

  const handleViewDocument = async () => {
    try {
      const res = await axios.get(`/users/me/bank-accounts/${id}/document`);
      if (res.data?.url) {
        window.open(res.data.url, '_blank', 'noopener,noreferrer');
      }
    } catch (e) {
      alert('Failed to get document URL. It may have expired or rate limit exceeded.');
    }
  };

  const renderBadge = (status) => {
    switch(status) {
      case 'verified': return <span className="px-2 py-1 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 text-xs font-medium rounded border border-emerald-300 dark:border-emerald-700">✓ Verified</span>;
      case 'rejected': return <span className="px-2 py-1 bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300 text-xs font-medium rounded border border-red-300 dark:border-red-700">Rejected</span>;
      default: return <span className="px-2 py-1 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 text-xs font-medium rounded border border-amber-300 dark:border-amber-700">Pending Review</span>;
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <ApiErrorModal
        isOpen={errorModal.isOpen}
        title={errorModal.title}
        message={errorModal.message}
        details={errorModal.details}
        onCancel={handleBack}
      />
      
      <div className="flex items-center gap-4 border-b border-slate-200 dark:border-slate-800 pb-4">
        <button 
          onClick={handleBack}
          className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400 transition-colors"
        >
          <svg className="w-5 h-5 rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Bank Account Details</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">View complete details of your connected bank account.</p>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-slate-500">Loading...</div>
      ) : bank ? (
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
          <div className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white">{bank.bank_name || 'Unknown Bank'}</h2>
                  {bank.is_primary && (
                    <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
                      Primary Account
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Country: {bank.country_code}</p>
              </div>
              <div>
                {renderBadge(bank.status)}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              <div>
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Account Holder Name</p>
                <p className="text-base text-slate-900 dark:text-white font-medium">{bank.account_holder_name}</p>
              </div>
              
              <div>
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Currency</p>
                <p className="text-base text-slate-900 dark:text-white font-medium">{bank.currency}</p>
              </div>

              <div>
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Account Number</p>
                <p className="text-base text-slate-900 dark:text-white font-mono">{bank.account_number}</p>
              </div>

              {bank.iban && (
                <div>
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">IBAN</p>
                  <p className="text-base text-slate-900 dark:text-white font-mono">{bank.iban}</p>
                </div>
              )}

              {bank.routing_code && (
                <div>
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Routing / Sort Code</p>
                  <p className="text-base text-slate-900 dark:text-white font-mono">{bank.routing_code}</p>
                </div>
              )}

              {bank.swift_bic && (
                <div>
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">SWIFT / BIC</p>
                  <p className="text-base text-slate-900 dark:text-white font-mono">{bank.swift_bic}</p>
                </div>
              )}

              {bank.branch_name && (
                <div>
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Branch Name</p>
                  <p className="text-base text-slate-900 dark:text-white">{bank.branch_name}</p>
                </div>
              )}

              {bank.branch_code && (
                <div>
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Branch Code</p>
                  <p className="text-base text-slate-900 dark:text-white font-mono">{bank.branch_code}</p>
                </div>
              )}
              
              <div className="md:col-span-2 pt-4 border-t border-slate-100 dark:border-slate-800 mt-2">
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-3">Verification Document</p>
                {bank.has_document ? (
                  <button 
                    onClick={handleViewDocument}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 rounded-lg text-sm font-medium transition-colors border border-indigo-200 dark:border-indigo-800/50"
                  >
                    <DocumentIcon className="w-4 h-4" />
                    View Uploaded Document
                  </button>
                ) : (
                  <p className="text-sm text-slate-500 dark:text-slate-400 italic">No document uploaded.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
